import { useState } from 'react';
import { verdictDeDepot } from '@groupe-trousseau/shared/lib';
import { useAnnuaire } from '../features/conversations/conversations';
import {
  useCreerUnDossier,
  useDeposerDesFichiers,
  useEspace,
  useModifierUnDossier,
  useOuvrirUnFichier,
  usePartagerUnDossier,
  useRetirerUnPartage,
  useSupprimerUnDossier,
  useSupprimerUnFichier,
} from '../features/espace/espace';
import { lireLErreur } from '../shared/api/client';
import { Bandeau } from '../shared/ui/Bandeau';
import { Bouton } from '../shared/ui/Bouton';
import { Carte } from '../shared/ui/Carte';
import { CaseACocher } from '../shared/ui/CaseACocher';
import { ChoixDeFichiers } from '../shared/ui/ChoixDeFichiers';
import { Saisie } from '../shared/ui/Champ';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { EtatVide } from '../shared/ui/EtatVide';
import { Jauge } from '../shared/ui/Jauge';
import { Selection } from '../shared/ui/Selection';
import { Squelette } from '../shared/ui/Squelette';
import { Statut } from '../shared/ui/Statut';
import { Titre } from '../shared/ui/Titre';
import { dateLisible } from '../shared/format/duree';
import { nomAffiche } from '../shared/format/designation';
import { poidsLisible } from '../shared/format/poids';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ESPACE DE FICHIERS — `CDC-3` F-33 À F-37.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **TROIS RÉGIMES DE DOSSIERS COEXISTENT, ET L'ÉCRAN LES SÉPARE** (F-33) : les siens ;
 * **deux dossiers tenus par le produit** ; et **les dossiers reçus, dans un emplacement à
 * part**. Les mélanger ferait proposer sur un dossier tenu des gestes qu'il refuse, et sur
 * un dossier reçu des gestes qui n'appartiennent pas à qui le lit.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE LES DEUX DOSSIERS TENUS PAR LE PRODUIT REFUSENT, ET POURQUOI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « À la racine, **ne se renomment pas, ne se déplacent pas, ne se suppriment pas, ne se
 * partagent pas.** » Le motif est écrit et il n'est pas le même pour les deux : partager
 * le dossier des documents fabriqués « exposerait **la totalité des cours passés** d'une
 * personne » ; partager celui des fichiers de conversation « exposerait **tous les fichiers
 * de toutes les conversations** » — et « ce refus-là **pèse plus lourd** ».
 *
 * **Le produit ne montre donc aucun de ces gestes sur ces dossiers.** Le serveur refuse de
 * toute façon, par `DOSSIER_TENU_PAR_LE_PRODUIT` ; montrer un bouton qui échoue ferait
 * découvrir la règle par un refus au lieu de la rendre lisible.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA JAUGE EST PERMANENTE, ET ELLE N'A AUCUN SEUIL D'AVERTISSEMENT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-36 : « **un indicateur permanent, en pourcentage du plafond**, est visible chaque fois
 * qu'on ouvre l'espace, y compris dans le panneau à côté du tableau. **Aucun seuil
 * d'avertissement intermédiaire.** » Il n'y a donc **rien à 80 %** : ni couleur, ni
 * bandeau, ni message. Le produit dit le pourcentage, et il ne commente pas.
 *
 * Et « **la personne est la seule à voir sa consommation** : celle qui règle le plafond ne
 * la voit pas, celle qui le subit la voit ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CET ÉCRAN NE FAIT PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Il ne visionne aucun document** (F-35) : « un clic ouvre le fichier dans un nouvel
 *   onglet, et un fichier se télécharge ». Aucune visionneuse, aucun aperçu.
 * - **Il ne fabrique aucune adresse publique** : le lien est présigné, à durée courte, et
 *   il se demande au clic.
 * - **Il ne propose aucune corbeille** : la suppression est immédiate, et sans retour.
 * - **Il n'exporte pas l'espace entier** (Q459).
 */
export function PageEspace() {
  const espace = useEspace();
  const [recherche, poserRecherche] = useState('');

  return (
    <main className="mx-auto flex w-full max-w-espace flex-col gap-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Titre niveau={1}>Fichiers</Titre>
        {espace.data !== undefined && (
          <span className="flex items-center gap-3">
            <Jauge
              pourcentage={espace.data.pourcentageDeRemplissage}
              gele={espace.data.pourcentageDeRemplissage >= 100}
            />
            <span className="text-13 text-texte-faible">
              {poidsLisible(Number(espace.data.poidsCourant))} sur{' '}
              {poidsLisible(Number(espace.data.plafondOctets))}
            </span>
          </span>
        )}
      </div>

      {espace.isLoading && <Squelette lignes={6} />}

      {espace.isError && (
        <EtatErreur
          message={lireLErreur(espace.error).message}
          onReessayer={() => void espace.refetch()}
        />
      )}

      {espace.data !== undefined && (
        <>
          {espace.data.pourcentageDeRemplissage >= 100 && (
            // « Au plafond, tout ajout est refusé. **Le gel se lève en retirant. Le
            // produit ne détruit jamais de lui-même.** »
            <Bandeau ton="echec">
              Votre espace est plein. Aucun dépôt n’est possible tant que vous
              n’avez pas retiré quelque chose. Le produit ne supprime rien de
              lui-même.
            </Bandeau>
          )}

          <Saisie
            libelle="Rechercher"
            aide="Sur le nom d’un fichier ou d’un dossier, dans votre espace et vos dossiers reçus."
            type="search"
            value={recherche}
            onChange={(evenement) => poserRecherche(evenement.target.value)}
          />

          <MesDossiers espace={espace.data} recherche={recherche.trim()} />
          <DossiersRecus espace={espace.data} recherche={recherche.trim()} />
        </>
      )}
    </main>
  );
}

type Espace = NonNullable<ReturnType<typeof useEspace>['data']>;
type DossierPropre = Espace['dossiers'][number];
type DossierRecu = Espace['dossiersRecus'][number];
type Fichier = DossierPropre['fichiers'][number];

/** La recherche « porte sur le nom, jamais sur le sens » (F-33). */
function correspond(nom: string, recherche: string): boolean {
  return recherche === '' || nom.toLowerCase().includes(recherche.toLowerCase());
}

/* ═════════════════════════════════════════════════════════════════════════
   Ses propres dossiers, et les deux que le produit tient
   ═════════════════════════════════════════════════════════════════════════ */

function MesDossiers({
  espace,
  recherche,
}: {
  readonly espace: Espace;
  readonly recherche: string;
}) {
  const creation = useCreerUnDossier();
  const [nomNeuf, poserNomNeuf] = useState('');

  // **Le tri est sur le nom** (F-33), et les dossiers tenus par le produit passent
  // devant : ils sont à la racine par règle, et les noyer dans l'ordre alphabétique
  // ferait chercher ce que le produit range tout seul.
  const dossiers = [...espace.dossiers].sort((a, b) => {
    if ((a.tenuParLeProduit === null) !== (b.tenuParLeProduit === null)) {
      return a.tenuParLeProduit === null ? 1 : -1;
    }
    return a.nom.localeCompare(b.nom, 'fr');
  });

  const visibles = dossiers.filter(
    (dossier) =>
      correspond(dossier.nom, recherche) ||
      dossier.fichiers.some((fichier) => correspond(fichier.nom, recherche)),
  );

  return (
    <section className="flex flex-col gap-3">
      <Titre niveau={2} taille="20">
        Votre espace
      </Titre>

      <Carte>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(evenement) => {
            evenement.preventDefault();
            if (nomNeuf.trim() === '') return;
            creation.mutate(
              { nom: nomNeuf.trim(), parentId: null },
              { onSuccess: () => poserNomNeuf('') },
            );
          }}
        >
          <span className="min-w-64 flex-1">
            <Saisie
              libelle="Nouveau dossier"
              value={nomNeuf}
              onChange={(evenement) => poserNomNeuf(evenement.target.value)}
            />
          </span>
          <Bouton type="submit" enCours={creation.isPending}>
            Créer
          </Bouton>
        </form>
        {creation.isError && (
          <div className="mt-3">
            <Bandeau ton="echec">{lireLErreur(creation.error).message}</Bandeau>
          </div>
        )}
      </Carte>

      {visibles.length === 0 ? (
        <EtatVide titre={recherche === '' ? 'Aucun dossier' : 'Rien ne correspond'}>
          {recherche === ''
            ? 'Créez un dossier pour commencer à ranger vos fichiers.'
            : 'La recherche porte sur le nom, jamais sur le contenu d’un document.'}
        </EtatVide>
      ) : (
        <ul className="flex flex-col gap-3">
          {visibles.map((dossier) => (
            <li key={dossier.id}>
              <BlocDossierPropre
                dossier={dossier}
                espace={espace}
                recherche={recherche}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BlocDossierPropre({
  dossier,
  espace,
  recherche,
}: {
  readonly dossier: DossierPropre;
  readonly espace: Espace;
  readonly recherche: string;
}) {
  const modification = useModifierUnDossier();
  const suppression = useSupprimerUnDossier();
  const [renommage, poserRenommage] = useState<string | null>(null);
  const tenu = dossier.tenuParLeProduit !== null;

  const fichiers = dossier.fichiers.filter(
    (fichier) => correspond(dossier.nom, recherche) || correspond(fichier.nom, recherche),
  );

  return (
    <Carte>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="flex flex-wrap items-center gap-3">
            <Titre niveau={3} taille="18">
              {dossier.nom}
            </Titre>
            {/* **Chacun des deux porte sa marque propre, jamais un drapeau commun**
                (Q569) : « sans cela, le produit ne peut pas décider où ranger un document
                fabriqué, où ranger un fichier envoyé, ni **lequel des deux porte le refus
                de partage le plus lourd** ». */}
            {dossier.tenuParLeProduit === 'documents-fabriques' && (
              <Statut teinte="neutre" pleine={false}>
                Tenu par le produit
              </Statut>
            )}
            {dossier.tenuParLeProduit === 'fichiers-de-conversation' && (
              <Statut teinte="neutre" pleine={false}>
                Tenu par le produit
              </Statut>
            )}
            {dossier.partageA.length > 0 && (
              <Statut teinte="attention" pleine={false}>
                {`Partagé à ${dossier.partageA.length}`}
              </Statut>
            )}
          </span>

          {/* **Aucun geste sur un dossier tenu par le produit**, et c'est la règle : ni
              renommer, ni déplacer, ni supprimer, ni partager. */}
          {!tenu && (
            <span className="flex flex-wrap gap-2">
              <Bouton
                variante="fantome"
                taille="petite"
                onClick={() =>
                  poserRenommage(renommage === null ? dossier.nom : null)
                }
              >
                Renommer ou déplacer
              </Bouton>
              <Bouton
                variante="fantome"
                taille="petite"
                enCours={suppression.isPending}
                onClick={() => suppression.mutate(dossier.id)}
              >
                Supprimer
              </Bouton>
            </span>
          )}
        </div>

        {!tenu && renommage !== null && (
          <form
            className="flex flex-wrap items-end gap-3 rounded-3 bg-fond-attenue p-3"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              modification.mutate(
                { id: dossier.id, nom: renommage.trim() },
                { onSuccess: () => poserRenommage(null) },
              );
            }}
          >
            <span className="min-w-64 flex-1">
              <Saisie
                libelle="Nom"
                value={renommage}
                onChange={(evenement) => poserRenommage(evenement.target.value)}
              />
            </span>
            <span className="min-w-64 flex-1">
              <Selection
                libelle="Dossier parent"
                aide="Un dossier partagé garde son partage quand il se déplace."
                value={dossier.parentId ?? ''}
                onChange={(evenement) =>
                  modification.mutate({
                    id: dossier.id,
                    parentId:
                      evenement.target.value === ''
                        ? null
                        : evenement.target.value,
                  })
                }
                options={[
                  { valeur: '', libelle: 'À la racine' },
                  // **Un dossier ne se déplace ni dans lui-même, ni dans un dossier
                  // reçu** : le second « lui ferait changer de propriétaire ». Les
                  // dossiers reçus ne sont donc pas dans cette liste, par construction.
                  ...espace.dossiers
                    .filter(
                      (autre) =>
                        autre.id !== dossier.id && autre.tenuParLeProduit === null,
                    )
                    .map((autre) => ({ valeur: autre.id, libelle: autre.nom })),
                ]}
              />
            </span>
            <Bouton type="submit" enCours={modification.isPending}>
              Renommer
            </Bouton>
          </form>
        )}

        {modification.isError && (
          <Bandeau ton="echec">{lireLErreur(modification.error).message}</Bandeau>
        )}
        {suppression.isError && (
          <Bandeau ton="echec">{lireLErreur(suppression.error).message}</Bandeau>
        )}

        <ListeDeFichiers fichiers={fichiers} />

        {/* **Le dépôt direct est interdit dans les deux dossiers tenus par le produit.** */}
        {!tenu && (
          <>
            <DepotDeFichiers dossierId={dossier.id} espace={espace} />
            <PartageDuDossier dossier={dossier} />
          </>
        )}
      </div>
    </Carte>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   Les dossiers reçus — un emplacement à part
   ═════════════════════════════════════════════════════════════════════════ */

function DossiersRecus({
  espace,
  recherche,
}: {
  readonly espace: Espace;
  readonly recherche: string;
}) {
  const visibles = espace.dossiersRecus.filter(
    (dossier) =>
      correspond(dossier.nom, recherche) ||
      dossier.fichiers.some((fichier) => correspond(fichier.nom, recherche)),
  );

  if (espace.dossiersRecus.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <Titre niveau={2} taille="20">
        Dossiers reçus
      </Titre>

      {/* « Un dossier reçu apparaît dans **un emplacement à part** : pas de parent, ne se
          déplace pas, ne se renomme pas » (Q446). Et il **ne pèse pas** ici : « il pèse
          chez le propriétaire du dossier, une seule fois » (Q441). */}
      <p className="text-13 text-texte-faible">
        Ces dossiers appartiennent à quelqu’un d’autre. Ce que vous y déposez lui
        appartient et pèse dans son espace, pas dans le vôtre.
      </p>

      {visibles.length === 0 ? (
        <p className="text-14 text-texte-faible">Rien ne correspond.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {visibles.map((dossier) => (
            <li key={dossier.id}>
              <BlocDossierRecu dossier={dossier} recherche={recherche} espace={espace} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BlocDossierRecu({
  dossier,
  recherche,
  espace,
}: {
  readonly dossier: DossierRecu;
  readonly recherche: string;
  readonly espace: Espace;
}) {
  const fichiers = dossier.fichiers.filter(
    (fichier) => correspond(dossier.nom, recherche) || correspond(fichier.nom, recherche),
  );

  return (
    <Carte>
      <div className="flex flex-col gap-4">
        <span className="flex flex-wrap items-center gap-3">
          <Titre niveau={3} taille="18">
            {dossier.nom}
          </Titre>
          {/* **Le prénom du propriétaire, et rien de plus.** */}
          <span className="text-13 text-texte-faible">
            de {nomAffiche(dossier.proprietaire.prenom)}
          </span>
          {/* « **Il voit combien de personnes ont accès au dossier, et leurs prénoms** —
              jamais davantage. C'est **un écart voulu au patron qui refuse l'information
              sans action** : ici elle en ouvre une, **savoir à qui l'on expose ce qu'on
              dépose**. » */}
          {dossier.partageA.length > 0 && (
            <Statut teinte="attention" pleine={false}>
              {`${dossier.partageA.length} ${
                dossier.partageA.length > 1
                  ? 'personnes ont accès'
                  : 'personne a accès'
              }`}
            </Statut>
          )}
        </span>

        <ListeDeFichiers fichiers={fichiers} />

        {/* « Le destinataire **lit et écrit DANS le dossier** : il ouvre, télécharge,
            prend un fichier pour un tableau, **dépose**, **crée des sous-dossiers**. Il
            n'écrit pas **SUR** le dossier. » */}
        <DepotDeFichiers dossierId={dossier.id} espace={espace} chezAutrui />
      </div>
    </Carte>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   Les fichiers, le dépôt, le partage
   ═════════════════════════════════════════════════════════════════════════ */

function ListeDeFichiers({ fichiers }: { readonly fichiers: readonly Fichier[] }) {
  const ouverture = useOuvrirUnFichier();
  const suppression = useSupprimerUnFichier();

  if (fichiers.length === 0) {
    // « **Un dossier vide subsiste** » (Q439) : il se montre vide, il ne disparaît pas.
    return <p className="text-13 text-texte-faible">Ce dossier est vide.</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {[...fichiers]
        .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
        .map((fichier) => (
          <li
            key={fichier.id}
            className="flex flex-wrap items-center justify-between gap-3 border-b border-bord pb-2 last:border-0"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-14 text-texte">{fichier.nom}</span>
              {/* « Une ligne de fichier montre **son nom, son poids, la date de son dépôt
                  et qui l'a déposé** » — « le poids parce qu'un plafond existe ; le
                  déposant parce qu'**un dossier partagé reçoit des fichiers de
                  plusieurs** ». */}
              <span className="text-13 text-texte-faible">
                {poidsLisible(fichier.poidsOctets)} · déposé le{' '}
                {dateLisible(fichier.deposeLe)} par{' '}
                {nomAffiche(fichier.deposePar.prenom)}
              </span>
            </span>
            <span className="flex shrink-0 gap-2">
              <Bouton
                variante="douce"
                taille="petite"
                enCours={ouverture.isPending}
                onClick={() =>
                  ouverture.mutate(fichier.id, {
                    onSuccess: (lien) => {
                      // « **Un clic ouvre le fichier dans un nouvel onglet**, et un
                      // fichier se télécharge. Le produit ne visionne aucun document. »
                      // `noopener` : la page ouverte ne doit pas pouvoir manipuler
                      // celle-ci, et l'adresse présignée n'est pas la nôtre.
                      window.open(lien.url, '_blank', 'noopener,noreferrer');
                    },
                  })
                }
              >
                Ouvrir
              </Bouton>
              <Bouton
                variante="fantome"
                taille="petite"
                enCours={suppression.isPending}
                onClick={() => suppression.mutate(fichier.id)}
              >
                Supprimer
              </Bouton>
            </span>
          </li>
        ))}
    </ul>
  );
}

function DepotDeFichiers({
  dossierId,
  espace,
  chezAutrui = false,
}: {
  readonly dossierId: string;
  readonly espace: Espace;
  readonly chezAutrui?: boolean;
}) {
  const depot = useDeposerDesFichiers();
  const [choisis, poserChoisis] = useState<readonly File[]>([]);

  const besoin = choisis.reduce((somme, fichier) => somme + fichier.size, 0);
  // **La règle vient de `shared/lib`, et le front la rejoue pour dire NON AVANT
  // d'envoyer** : « téléverser trois mégaoctets pour apprendre au retour qu'ils ne
  // rentraient pas fait payer l'attente à la personne, sur un refus que le produit
  // connaissait d'avance. **Le serveur refuse quand même** — c'est lui qui fait
  // autorité, et une garde de front ne garde rien. »
  //
  // **Sur un dossier reçu, la garde ne vaut PAS** : « le gel porte sur l'espace où le
  // fichier atterrit », et ce n'est pas le nôtre — nous ne connaissons pas le plafond du
  // propriétaire, et il ne nous regarde pas. Le serveur seul tranche.
  const verdict = chezAutrui
    ? { accepte: true as const }
    : verdictDeDepot(
        Number(espace.poidsCourant),
        besoin,
        Number(espace.plafondOctets),
      );

  return (
    <div className="flex flex-col gap-3 border-t border-bord pt-3">
      <ChoixDeFichiers
        libelle="Déposer des fichiers"
        aide="Plusieurs d’un coup. Un dossier entier ne se dépose pas."
        onChoisir={poserChoisis}
      />

      {!verdict.accepte && (
        // « Au plafond, tout ajout est refusé, avec un message qui dit **la place
        // restante et ce que le dépôt demande** » — les deux, et pas un pourcentage :
        // « un pourcentage ne dit pas si un document de trois mégaoctets entre ».
        <Bandeau ton="echec">
          Ce dépôt demande {poidsLisible(verdict.besoin)} et il vous reste{' '}
          {poidsLisible(verdict.placeRestante)}. Retirez quelque chose pour faire
          de la place.
        </Bandeau>
      )}

      {choisis.length > 0 && verdict.accepte && (
        <span>
          <Bouton
            enCours={depot.isPending}
            onClick={() =>
              depot.mutate(
                { dossierId, fichiers: choisis },
                { onSuccess: () => poserChoisis([]) },
              )
            }
          >
            Déposer {choisis.length} fichier{choisis.length > 1 ? 's' : ''}
          </Bouton>
        </span>
      )}

      {/* **La création partielle avec signalement se LIT** : les deux listes, et jamais
          une seule. « Quand un fichier du lot est refusé, les autres entrent, et le
          produit nomme ceux qui n'ont pas pu, avec leur raison. » */}
      {depot.data !== undefined && depot.data.refuses.length > 0 && (
        <Bandeau ton="attention">
          {depot.data.entres.length > 0 &&
            `${depot.data.entres.length} fichier${depot.data.entres.length > 1 ? 's sont entrés' : ' est entré'}. `}
          Ceux-ci n’ont pas pu&nbsp;:{' '}
          {depot.data.refuses
            .map((refuse) => `${refuse.nom} (${refuse.raison})`)
            .join(', ')}
          .
        </Bandeau>
      )}

      {depot.isError && (
        <Bandeau ton="echec">{lireLErreur(depot.error).message}</Bandeau>
      )}
    </div>
  );
}

function PartageDuDossier({ dossier }: { readonly dossier: DossierPropre }) {
  const annuaire = useAnnuaire();
  const partage = usePartagerUnDossier();
  const retrait = useRetirerUnPartage();
  const [ouvert, poserOuvert] = useState(false);
  const [choisis, poserChoisis] = useState<readonly string[]>([]);

  // « **Le partage n'est possible qu'avec une personne en relation.** Sans relation, il
  // est refusé. » Les relations sont exactement les correspondants de l'annuaire, et il
  // n'existe aucune autre liste de personnes dans ce produit.
  const relations = (annuaire.data?.lignes ?? []).flatMap(
    (ligne) => ligne.correspondants,
  );

  return (
    <div className="flex flex-col gap-3 border-t border-bord pt-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-14 font-weight-700 text-texte-fort">Partage</span>
        <Bouton
          variante="fantome"
          taille="petite"
          onClick={() => poserOuvert(!ouvert)}
        >
          {ouvert ? 'Fermer' : 'Partager ce dossier'}
        </Bouton>
      </div>

      {dossier.partageA.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {dossier.partageA.map((acces) => (
            <li key={acces.partageId} className="flex items-center gap-1">
              <Statut teinte="neutre" pleine={false}>
                {nomAffiche(acces.prenom)}
              </Statut>
              {/* « **Seul celui qui a partagé retire le partage.** Le retrait est
                  immédiat, porte sur le dossier et tout son contenu, **et ne rend rien**
                  au destinataire de ce qu'il y avait déposé. » */}
              <Bouton
                variante="fantome"
                taille="petite"
                enCours={retrait.isPending}
                onClick={() => retrait.mutate(acces.partageId)}
              >
                Retirer
              </Bouton>
            </li>
          ))}
        </ul>
      )}

      {ouvert && (
        <div className="flex flex-col gap-3 rounded-3 bg-fond-attenue p-3">
          {/* « **On partage à plusieurs personnes en un geste**, choisies parmi ses
              relations ; le produit crée **autant de partages distincts, retirables
              séparément**. » */}
          <fieldset className="flex flex-col gap-2">
            <legend className="text-14 font-weight-700 text-texte-fort">
              À qui
            </legend>
            {relations.length === 0 ? (
              <p className="text-13 text-texte-faible">
                Vous n’avez aucune relation avec qui partager.
              </p>
            ) : (
              relations.map((personne) => (
                <CaseACocher
                  key={personne.id}
                  libelle={nomAffiche(personne.prenom)}
                  coche={choisis.includes(personne.id)}
                  onChanger={(coche) =>
                    poserChoisis(
                      coche
                        ? [...choisis, personne.id]
                        : choisis.filter((id) => id !== personne.id),
                    )
                  }
                />
              ))
            )}
          </fieldset>

          {/* **Aucun email au partage ni au retrait** (Q459), et le coût est écrit : « un
              dossier partagé peut rester ignoré longtemps ». L'écran le dit, parce que
              sinon la personne croit avoir prévenu quelqu'un. */}
          <p className="text-13 text-texte-faible">
            Personne n’est prévenu par email, ni au partage ni au retrait.
          </p>

          {partage.isError && (
            <Bandeau ton="echec">{lireLErreur(partage.error).message}</Bandeau>
          )}

          <span>
            <Bouton
              enCours={partage.isPending}
              disabled={choisis.length === 0}
              onClick={() =>
                partage.mutate(
                  { dossierId: dossier.id, destinatairesIds: choisis },
                  {
                    onSuccess: () => {
                      poserChoisis([]);
                      poserOuvert(false);
                    },
                  },
                )
              }
            >
              Partager
            </Bouton>
          </span>
        </div>
      )}
    </div>
  );
}
